import { takeLatest, put, call, fork, race, take } from 'redux-saga/effects';
import { fetchDashboard } from 'common/dashboard/containers/dashboard-actions';
import callApi from 'utils/api';
import * as actionTypes from '../constants';
import {
  saveTicketSuccess,
  saveTicketFailure,
  createTicketSuccess,
  createTicketFailure,
  getTicketSuccess,
  getTicketFailure,
  addCommentSuccess,
  addCommentFailure,
  getTicketActionTypesSuccess,
  getTicketActionTypesFailure,
  getCategoryInfoSuccess,
  getCategoryInfoFailure,
  getCommentsByTicketIdSuccess,
  getCommentsByTicketIdFailure,
  getTicketStatusSuccess,
  getTicketStatusFailure,
  getTicketStatus,
  snoozeTicketSuccess,
  snoozeTicketFailure,
} from './ticket-form-actions';

export function* doCreateTicket(action) {
  try {
    const results = yield call(
      callApi,
      { endpoint: `ticket/new/${action.payload.id}/${action.payload.type}` },
    );
    yield put(createTicketSuccess(results));
  } catch (err) {
    yield put(createTicketFailure(err));
  }
}

export function* doSaveTicket(action) {
  try {
    const { data, reload, callback } = action.payload;

    const { results, navigatedAway } = yield race({
      results: call(callApi, {
        endpoint: 'ticket/save',
        options: {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
          errorMessage: 'Could not update ticket',
          retryAction: action,
        },
      }),
      navigatedAway: take('LOCATION_CHANGE'),
    });
    if (!navigatedAway) {
      yield put(saveTicketSuccess(results));
      if (callback) {
        yield put(callback);
      }
      if (reload && reload.type !== 'order') {
        const { id, type } = reload;
        yield put(fetchDashboard({ id, type }));
      }
    }
  } catch (err) {
    yield put(saveTicketFailure(err));
  }
}

export function* doAddComment(action) {
  try {
    const { results, navigatedAway } = yield race({
      results: call(callApi, {
        endpoint: 'ticket/comment/new',
        options: {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(action.payload),
          errorMessage: 'Could not add comment',
          retryAction: action,
        },
      }),
      navigatedAway: take('LOCATION_CHANGE'),
    });
    if (!navigatedAway) {
      yield put(addCommentSuccess(results));
      yield put(getTicketStatus({ id: action.payload.ticketId }));
    }
  } catch (err) {
    yield put(addCommentFailure(err));
  }
}

export function* doGetCategoryInfo(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `ticket/${action.payload.id}/info`,
      options: {
        errorMessage: 'Could not get category info',
        retryAction: action,
      },
    });
    yield put(getCategoryInfoSuccess(results));
  } catch (err) {
    yield put(getCategoryInfoFailure(err));
  }
}

export function* doGetCommentsByTicketId(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `ticket/comment/${action.payload}`,
      options: {
        errorMessage: 'Could not get comments by id',
        retryAction: action,
      },
    });
    yield put(getCommentsByTicketIdSuccess(results));
  } catch (err) {
    yield put(getCommentsByTicketIdFailure(err));
  }
}

export function* doGetTicket(action) {
  try {
    const results = yield call(callApi, {
      // eslint-disable-next-line
      endpoint: `ticket/${action.payload.id}?isNew=${action.payload.isNew}`,
      options: {
        errorMessage: 'Could not get ticket',
        retryAction: action,
      },
    });
    yield put(getTicketSuccess({ ...results, isNew: action.payload.isNew }));
  } catch (err) {
    yield put(getTicketFailure(err));
  }
}

export function* doGetTicketStatus(action) {
  const { id } = action.payload;
  try {
    const results = yield call(callApi, {
      endpoint: `ticket/${id}/status`,
      options: {
        errorMessage: 'Could not get ticket status',
        retryAction: action,
      },
    });
    yield put(getTicketStatusSuccess(results));
  } catch (err) {
    yield put(getTicketStatusFailure(err));
  }
}

export function* doSnoozeTicket(action) {
  const { id, comment, snoozeUntil } = action.payload;
  const request = {
    comment,
    snoozeUntil,
  };
  try {
    const results = yield call(callApi, {
      endpoint: `ticket/${id}/snooze`,
      options: {
        method: 'POST',
        body: JSON.stringify(request),
        errorMessage: 'Could not snooze ticket',
        retryAction: action,
      },
    });
    yield put(snoozeTicketSuccess(results));
  } catch (err) {
    yield put(snoozeTicketFailure(err));
  }
}

export function* doGetTicketActionTypes(action) {
  try {
    const { id, objectType } = action.payload;
    const results = yield call(callApi, {
      endpoint: `ticket/action/definitions?metamodel=${objectType}&ticketId=${id}`,
    });
    yield put(getTicketActionTypesSuccess(results));
  } catch (err) {
    yield put(getTicketActionTypesFailure(err));
  }
}

function* watchSaveTicket() {
  yield takeLatest(actionTypes.SAVE_TICKET, doSaveTicket);
}

function* watchGetTicket() {
  yield takeLatest(actionTypes.GET_TICKET, doGetTicket);
  yield takeLatest(actionTypes.GET_TICKET_ACTION_TYPES, doGetTicketActionTypes);
}

function* watchGetCategoryInfo() {
  yield takeLatest(actionTypes.GET_CATEGORY_INFO, doGetCategoryInfo);
}

function* watchGetCommentsByTicketId() {
  yield takeLatest(actionTypes.GET_COMMENTS_BY_TICKET_ID, doGetCommentsByTicketId);
}

function* watchCreateTicket() {
  yield takeLatest(actionTypes.CREATE_TICKET, doCreateTicket);
}

function* watchAddComment() {
  yield takeLatest(actionTypes.ADD_COMMENT, doAddComment);
}

function* watchGetTicketStatus() {
  yield takeLatest(actionTypes.GET_TICKET_STATUS, doGetTicketStatus);
}

function* watchSnoozeTicketRequest() {
  yield takeLatest(actionTypes.SNOOZE_TICKET_REQUEST, doSnoozeTicket);
}

export default [
  fork(watchGetCategoryInfo),
  fork(watchCreateTicket),
  fork(watchSaveTicket),
  fork(watchGetTicket),
  fork(watchAddComment),
  fork(watchGetCommentsByTicketId),
  fork(watchGetTicketStatus),
  fork(watchSnoozeTicketRequest),
];
